import React, { useEffect, useState } from 'react';
import { isEmpty, isNil, sumBy } from 'lodash';
import { connect } from 'react-redux';
// @ts-ignore
import { fmtMoney, fmtPercent, fmtPrice } from '@upstart/harmony/lib/formatter';
import {
  Alert,
  Button,
  Col,
  Grid,
  Row,
  Tab,
  Table,
  Tabs,
} from 'react-bootstrap';
import { useAsyncCallback } from 'react-async-hook';
import { useParams } from 'react-router-dom';
import { bindActionCreators, Dispatch } from 'redux';
import { Car } from '@upstart/harmony/lib/legos/carTypes';
import { Deal, DealId } from '@upstart/harmony/lib/legos/dealTypes';
import {
  FinanceResponseAudit,
  UpstartApplicationStatus,
} from '@upstart/harmony/lib/legos/financingTypes';
import { User } from '@upstart/harmony/lib/legos/userTypes';
import { DealerWithFlags } from '@upstart/harmony/lib/legos/dealerTypes';
import {
  FinanceResponseStatus,
  UPSTART_MIN_FINANCE_AMOUNT,
} from '@upstart/harmony/lib/legos/constants';
import { ProductId } from '@upstart/harmony/lib/legos/penTypes';
import { getPrimarySelectedCar } from 'redux/deals/selectors';
import { calculateFinalPrice } from '@upstart/harmony/lib/pen-utils';
import {
  getTradeInAllowance,
  getTradeInPayoff,
} from '@upstart/harmony/lib/dealer-utils';
import { Loader } from 'semantic-ui-react';
import WebsocketService from '@upstart/harmony/lib/websocket-service';
import {
  DealCreatedOrUpdatedWebsocketEventFilter,
  EventType,
} from '@upstart/harmony/lib/types/websocketTypes';
import { DMSType } from '@upstart/harmony/lib/legos/dmsTypes';
import {
  getMaxAllowableRate,
  isCounter as isCounterResponse,
  isResponseEditable,
} from '@upstart/harmony/lib/response-utils';
import {
  LendingStatusBadge,
  LendingStatusBadgeEnum,
  LendingValueBadge,
} from '@upstart/desking-components';

import { ProductMap } from 'util/protectionPlans';
import useFinanceResponsePayments from '../useFinanceResponsePayments';
import FinanceResponseService from '../../../services/FinanceResponseService';
import useAccessories from '../../../components/PencilEditor/useAccessories';
import AccessoryList from '../../../components/PencilEditor/AccessoryList';
import useProtectionPlans from '../../../components/PencilEditor/useProtectionPlans';
import useFetchDealOwner from '../../../components/PencilEditor/useFetchDealOwner';
import ProtectionPlansList from '../../../components/PencilEditor/ProtectionPlansList';
import PriceInput from '../../../components/PencilEditor/Input/PriceInput';
import { Select } from '../../../components/PencilEditor';
import dealsActions from '../../../redux/deals/actions';
import penActions from '../../../redux/pen/actions';
import FinanceApplicationService from '../../../services/FinanceApplicationService';
import AddOnsList from '../../../components/PencilEditor/AddOnsList';
import Tooltip from '../../../components/Tooltip';
import { isDMSFlowEnabled as isDMSFlowEnabledUtil } from '../../../util/dmsUtils';

import { UpstartFinanceDetails } from '../UpstartFinanceDetails';
import { DownPaymentTooltip } from '../DownPaymentTooltip';
import { OfferHistoryModal } from '../OfferHistory/OfferHistoryModal';
import { useUpstartRates } from '../useUpstartRates';
import TradeInsModal from '../../../components/DeskingEditor/TradeInsModal';
import { TradeInField } from '../../../components/DeskingEditor/TradeInsModal/TradeInsTableRow';
import {
  getUpstartOfferEventProperties,
  sendData,
} from '../../../util/amplitude';
import { amplitudeEventsDeskingCTR } from '../../../util/amplitudeEvents';
import {
  getLendingValueBadgeEnum,
  isAutoAdjustedCounterOffer,
} from '../../../util/financeResponseUtils';
import { OfferSummaryContent } from './OfferSummaryContent';

const EXCLUDED_CODES = ['miscellaneous'];

const tradeInFields: TradeInField[] = [
  {
    isEditable: true,
    key: 'id',
  },
  {
    isEditable: true,
    key: 'year',
  },
  {
    isEditable: true,
    key: 'make',
  },
  {
    isEditable: true,
    key: 'model',
  },
  {
    isEditable: true,
    key: 'trim',
  },
  {
    isEditable: true,
    key: 'payOff',
  },
  {
    isEditable: true,
    key: 'tradeInAllowance',
  },
];

interface Params {
  dealId: string;
  responseId: string;
}

interface Props {
  car?: Car;
  deal?: Deal;
  user?: User;
  dealer?: DealerWithFlags;
  isLoading?: boolean;
  actions: {
    dealRequest: ({ dealId }: { dealId: DealId }) => void;
  };
  dealId: string;
  responseId: string;
  reloadResponses: VoidFunction;
}

const getCarInfo = (car?: Car) =>
  `${car?.year} ${car?.make} ${car?.model} ${car?.trim}`;

const getVehicleSalesPriceInfo = (car?: Car) =>
  `Sales price for customer vehicle: ${getCarInfo(car)}`;

function UpstartOffer(props: Props) {
  const params = useParams<Params>();
  const [isEditing, setIsEditing] = useState(false);
  const [isMissingAddonFields, setIsMissingAddonFields] = useState(false);

  const responseId = params.responseId || props.responseId;
  const [showTradeInsModal, setShowTradeInsModal] = useState(false);
  const [showOfferHistoryModal, setShowOfferHistoryModal] = useState(false);

  const dealId = params.dealId || props.dealId;
  const { dealer } = props;
  const {
    response,
    setResponse,
    originalResponse,
    resetOriginalResponse,
    loadingResponse,
    loadingPayments: fetchingPayments,
    responseError,
    paymentsError,
    fetchResponseWithUpdatedData,
  } = useFinanceResponsePayments({ responseId });

  const asyncUpstartApplicationStatus = useAsyncCallback<
    UpstartApplicationStatus
  >(FinanceApplicationService.getApplicationStatus);

  useEffect(() => {
    if (response?.applicationId) {
      asyncUpstartApplicationStatus.execute(response.applicationId);
    }
    // eslint-disable-next-line
  }, [response?.applicationId]);

  const stipulations = (
    asyncUpstartApplicationStatus.result?.stipulations ?? []
  ).filter(
    stipulation =>
      stipulation.code && !EXCLUDED_CODES.includes(stipulation.code),
  );
  const isSigned = asyncUpstartApplicationStatus.result?.signedOn ?? false;

  const hasStipulationGenerationCompleted =
    asyncUpstartApplicationStatus.result?.hasStipulationGenerationCompleted ??
    false;
  const hasUpdateInProgress =
    asyncUpstartApplicationStatus.result?.hasUpdateInProgress ?? false;

  const fetchApplicationStatus = () => {
    if (response) {
      asyncUpstartApplicationStatus.execute(response.applicationId);
    }
  };

  useEffect(() => {
    const dealerId = props.deal?.dealer_id ?? '';
    const wsEvent: DealCreatedOrUpdatedWebsocketEventFilter = {
      dealerId,
      eventType: EventType.DEAL_CREATED_OR_UPDATED,
    };

    WebsocketService.subscribe(wsEvent, fetchApplicationStatus);

    return () => WebsocketService.unsubscribe(wsEvent);
    // eslint-disable-next-line
  }, []);

  const updateFinanceResponse = useAsyncCallback(FinanceResponseService.update);
  const selectedAddOns = response?.data?.selectedAddOns ?? [];
  const selectedAccessories = response?.data?.selectedAccessories ?? [];
  const selectedBundles = response?.data?.selectedBundles ?? [];
  const selectedProducts = response?.data?.selectedProducts ?? [];
  const priceOverrides =
    response?.data?.protectionPlanProductPriceOverrides ?? {};
  const tradeIns = response?.data?.tradeIns ?? [];
  const tradeInAllowance = getTradeInAllowance(tradeIns);
  const tradePayoff = getTradeInPayoff(tradeIns);
  const netTrade = tradeInAllowance - tradePayoff;

  const validations = isEditing
    ? response?.validations
    : originalResponse?.validations;
  const valueBadgeEnum = getLendingValueBadgeEnum(
    response,
    validations?.isInvalid,
  );
  const isCounter = !!response && isCounterResponse(response);
  const needsAdjustment = isCounter && response?.validations?.isInvalid;
  const responseIsAdjustedCounterOffer =
    originalResponse && isAutoAdjustedCounterOffer(originalResponse);
  const {
    allRates,
    highlightedRate,
    termRates,
    financeAmountRates,
    downPaymentRates,
    loadingRates,
  } = useUpstartRates(response);

  const maxAllowableRate = getMaxAllowableRate(allRates);
  const maxAllowableFinanceAmount = maxAllowableRate?.maxFinanceAmount ?? 0;

  useEffect(() => {
    if (needsAdjustment) {
      setIsEditing(true);
    }
  }, [response]);

  const {
    fetching: accessoriesFetching,
    accessories: applicableAccessories,
    toggleAccessory,
    totalAccessoryPrice,
  } = useAccessories({
    selected: selectedAccessories,
    car: props.car,
    dealer,
  });

  const { dealOwner } = useFetchDealOwner({ car: props.car, dealer });

  const {
    rates,
    bundles,
    fetching: protectionPlansFetching,
    productList,
    termOptions,
    toggleProductSelect,
    products,
    setProducts,
    minMaxDebounceInEffect,
    setMinMaxDebounceInEffect,
    productIdBeingEdited,
    setProductIdBeingEdited,
    getSelectedBundles,
    getSelectedProducts,
  } = useProtectionPlans({
    car: props.car,
    deal: props.deal,
    dealer: dealOwner,
    financeType: props.deal?.financeType,
    protectionPlanProductPriceOverrides: priceOverrides,
    selectedBundles,
    selectedProducts,
    isOnePayLease: false,
  });

  const totalSelectedAddOnPrice = sumBy(selectedAddOns, 'price');

  useEffect(() => {
    if (dealId) {
      props.actions.dealRequest({ dealId });
    }
  }, [dealId, props.actions]);

  const totalProductPrice = selectedProducts.reduce(
    (total, product) =>
      total +
      calculateFinalPrice(product, selectedBundles, priceOverrides, props.deal),
    0,
  );

  const fetchingError = !isNil(
    responseError || paymentsError || updateFinanceResponse.error,
  );

  const effectivePrice =
    response?.data?.effectivePrice ??
    response?.FinanceApplication?.data?.effectivePrice ??
    0;
  const financeAmount = response?.data?.financeAmount ?? 0;
  const isEditable = isResponseEditable(response);
  const isDMSFlowEnabled = (dealer && isDMSFlowEnabledUtil(dealer)) ?? false;
  const isDMSTypeCDK = isDMSFlowEnabled && dealer?.dmsType === DMSType.CDK;

  const missingAddOnFields = selectedAddOns.find(
    addOn =>
      isEmpty(addOn.description) ||
      (isDMSFlowEnabled && isDMSTypeCDK && isEmpty(addOn.dmsCode)),
  );

  const saveChanges = () => {
    if (missingAddOnFields) {
      setIsMissingAddonFields(true);
    } else {
      setIsMissingAddonFields(false);
      if (!response) {
        return;
      }
      const updatedResponse = { ...response };
      if (isCounter) {
        updatedResponse.status = FinanceResponseStatus.APPROVED;
      }
      updateFinanceResponse.execute(updatedResponse).then(() => {
        setIsEditing(false);
        fetchResponseWithUpdatedData({});
        resetOriginalResponse();
        // Reload responses to make sure changes are captured in list
        props.reloadResponses();
      });
    }
  };

  useEffect(() => {
    if (!missingAddOnFields) {
      setIsMissingAddonFields(false);
    }
  }, [selectedAddOns]);

  const cancelChanges = () => {
    if (response) {
      setResponse(originalResponse);
    }
  };

  const asyncAuditResponse = useAsyncCallback<FinanceResponseAudit>(
    FinanceResponseService.getAudit,
  );

  const onOfferHistoryClick = () => {
    asyncAuditResponse.execute(response?.id);
    setShowOfferHistoryModal(true);
  };

  const isOverLimit = !!validations?.isFinanceAmountOverLimit;
  const isUnderLimit = !!validations?.isFinanceAmountUnderLimit;

  const hasGapRelatedError = !!(
    validations?.isLTVBelowMinimumForGap || validations?.isGapPremiumOverLimit
  );

  const showDownPaymentError =
    // If the down payment is invalid for all rates
    validations?.isDownPaymentInvalidForAllRates ||
    // If the down payment is invalid for the current rate because it would push the finance amount out of bounds
    (validations?.isDownPaymentInvalid && !validations?.isFinanceAmountInvalid);

  const statusBadge = isCounter ? (
    <LendingStatusBadge statusBadge={LendingStatusBadgeEnum.APPROVED_COUNTER} />
  ) : (
    <LendingStatusBadge statusBadge={LendingStatusBadgeEnum.APPROVED} />
  );

  const valueBadge = valueBadgeEnum ? (
    <LendingValueBadge valueBadge={valueBadgeEnum} />
  ) : (
    undefined
  );

  const productsAccessoriesAddOnsColumn = (
    <Col
      xs={responseIsAdjustedCounterOffer ? 6 : 12}
      sm={6}
      className="offer-section"
    >
      {selectedAccessories.length > 0 && (
        <>
          <div className="offer-section-title">Selected Accessories</div>
          <div className="products-table-container">
            <Table>
              <tbody>
                {selectedAccessories.map(accessory => (
                  <tr key={accessory.id}>
                    <td>{accessory.name}</td>
                    <td className="b text-right">
                      {fmtPrice(accessory.price ?? 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </>
      )}
      {selectedProducts.length > 0 && (
        <>
          <div className="offer-section-title">Selected Protection Plans</div>
          <div className="products-table-container">
            <Table>
              <tbody>
                {selectedProducts.map(product => {
                  const finalPrice = calculateFinalPrice(
                    product,
                    selectedBundles,
                    priceOverrides,
                    props.deal,
                  );

                  if (finalPrice === 0) {
                    return null;
                  }

                  return (
                    <tr key={product.productId}>
                      <td>{product.displayName}</td>
                      <td className="b text-right">{fmtPrice(finalPrice)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </>
      )}
      {selectedAddOns.length > 0 && (
        <>
          <div className="offer-section-title">Selected Add Ons</div>
          <div className="products-table-container">
            <Table>
              <tbody>
                {selectedAddOns.map(addOn => (
                  <tr key={`${addOn.description}_${addOn.dmsCode}`}>
                    <td>{addOn.description}</td>
                    <td className="b text-right">{fmtPrice(addOn.price)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </>
      )}

      {selectedAccessories.length > 0 ||
      selectedProducts.length > 0 ||
      selectedAddOns.length > 0 ? (
        <div className="text-right pt-15 pr-5">
          Total:{' '}
          {fmtPrice(
            totalAccessoryPrice + totalProductPrice + totalSelectedAddOnPrice,
          )}
        </div>
      ) : (
        <div className="text-center pt-15 pr-5">No products selected.</div>
      )}
    </Col>
  );

  return (
    <>
      <div>
        {props.isLoading || loadingResponse ? (
          <Alert>Loading...</Alert>
        ) : (
          <div className="upstart-offer-container">
            {fetchingError && (
              <Alert bsStyle="danger">
                Failed to fetch data. Please try again.
              </Alert>
            )}
            {!loadingResponse && (
              <>
                {isOverLimit && !isCounter && (
                  <Alert bsStyle="danger">
                    Based on your recent change, the current finance amount
                    exceeds the maximum approved finance amount. Please make
                    adjustments to fall back into the approved finance amount.
                  </Alert>
                )}
                {isUnderLimit && (
                  <Alert bsStyle="danger">
                    Based on your recent change, the current finance amount does
                    not meet the minimum allowed finance amount (
                    {fmtMoney(
                      validations?.minFinanceAmountLimit ??
                        UPSTART_MIN_FINANCE_AMOUNT,
                      2,
                    )}
                    ). Please make adjustments to fall back into the approved
                    finance amount.
                  </Alert>
                )}
                {validations?.isDownPaymentOverLimit &&
                  !validations?.isFinanceAmountInvalid && (
                    <Alert bsStyle="danger">
                      Based on your recent change, the current down payment
                      amount exceeds the maximum approved down payment amount.
                      Please make adjustments to fall back into the approved
                      down payment amount.
                    </Alert>
                  )}
                {validations?.isDownPaymentUnderLimit &&
                  !validations?.isFinanceAmountInvalid && (
                    <Alert bsStyle="danger">
                      Based on your recent change, the current down payment
                      amount does not meet the minimum allowed down payment
                      amount. Please make adjustments to fall back into the
                      approved down payment amount.
                    </Alert>
                  )}
                {response && !loadingRates && !highlightedRate?.apr && (
                  <Alert bsStyle="danger">
                    Based on your recent change, the current finance amount does
                    not meet the qualifications for any offer. Please make
                    adjustments to fall back into an approved finance amount.
                  </Alert>
                )}
                {validations && validations.isBackendOverLimit && (
                  <Alert bsStyle="danger">
                    Based on your recent change, the current backend amount
                    exceeds the maximum approved backend amount for the selected
                    rate. Please make adjustments to fall back into the approved
                    backend amount.
                  </Alert>
                )}
                {hasGapRelatedError &&
                  ((validations.isLTVBelowMinimumForGap && (
                    <Alert bsStyle="danger">
                      State law does not allow for a GAP waiver to be added to a
                      vehicle with the current vehicle’s LTV. Please remove the
                      GAP Waiver in order to proceed with this loan.
                    </Alert>
                  )) ||
                    (validations.isGapPremiumOverLimit && (
                      <Alert bsStyle="danger">
                        GAP waiver exceeds state allowable limits of premium
                        amount to amount financed. Please remove the GAP Waiver
                        or reduce the premium amount to be less than{' '}
                        {fmtMoney(validations?.maxGapPremium ?? 0, 2)} in order
                        to proceed with this loan.
                      </Alert>
                    )))}
              </>
            )}
            {isMissingAddonFields && (
              <Alert bsStyle="danger">
                Based on your recent change, an add on is missing required
                information.
              </Alert>
            )}
            {highlightedRate?.requiresCreditUnionMembership && (
              <Alert bsStyle="info">
                <b>Membership Required</b> Upstart has partnered with a credit
                union for your offer. This option will require you to become a
                member of the credit union, which will be included during the
                contract signing process.
              </Alert>
            )}
            <Grid>
              <Row className="">
                <Col md={4} sm={5} className="p-0">
                  <div className="flex items-center pt-15">
                    <h4 className="b pr-15 pt-5 m-0">Upstart Finance Offer</h4>
                    <span style={{ marginRight: '5px' }}>{statusBadge}</span>
                    <span>{valueBadge}</span>
                  </div>
                  <div
                    className="pt-5 offer-history"
                    onClick={() => onOfferHistoryClick()}
                  >
                    Offer History
                  </div>
                </Col>

                {!isSigned && (
                  <Col sm={3} className="p-0 float-right text-right">
                    {isEditing ? (
                      <>
                        {!needsAdjustment && (
                          <Col sm={6} className="button-wrapper">
                            <Button
                              className="w-100 cancel-button"
                              onClick={() => {
                                setIsEditing(false);
                                cancelChanges();
                              }}
                            >
                              Cancel Changes
                            </Button>
                          </Col>
                        )}
                        <Col sm={6} className="float-right button-wrapper">
                          <Button
                            bsStyle="primary"
                            className="w-100 submit-button"
                            disabled={
                              fetchingPayments ||
                              updateFinanceResponse.loading ||
                              fetchingError ||
                              isOverLimit ||
                              isMissingAddonFields ||
                              isUnderLimit ||
                              !isEditable ||
                              validations?.isInvalid
                            }
                            onClick={() => {
                              sendData(
                                amplitudeEventsDeskingCTR.UPSTART_OFFER_SAVE_DETAILS_CLICKED,
                                getUpstartOfferEventProperties({
                                  dealId,
                                  application: response?.FinanceApplication,
                                  response,
                                }),
                              );
                              saveChanges();
                            }}
                          >
                            Save Offer
                          </Button>
                        </Col>
                      </>
                    ) : (
                      isEditable && (
                        <Col sm={6} className="float-right p-0">
                          <button
                            className="pencil-button pencil-header-button capitalize-in-chairman"
                            onClick={() => {
                              sendData(
                                amplitudeEventsDeskingCTR.UPSTART_OFFER_EDIT_DETAILS_CLICKED,
                                getUpstartOfferEventProperties({
                                  dealId,
                                  application: response?.FinanceApplication,
                                  response,
                                }),
                              );
                              // Initial call to validate data before starting to edit
                              fetchResponseWithUpdatedData({});
                              setIsEditing(true);
                            }}
                          >
                            <i className="glyphicon glyphicon-pencil edit-markup" />
                            Edit details
                          </button>
                        </Col>
                      )
                    )}
                  </Col>
                )}
              </Row>
              <Row className="pt-15 pb-15 flex flex-wrap">
                <OfferSummaryContent
                  originalResponse={originalResponse}
                  response={response}
                  isEditable={isEditable}
                  isEditing={isEditing}
                  responseIsAutoAdjustedCounter={
                    !!responseIsAdjustedCounterOffer
                  }
                  financeAmount={financeAmount}
                  fetchingPayments={fetchingPayments}
                  maxAllowableRate={maxAllowableRate}
                  maxAllowableFinanceAmount={maxAllowableFinanceAmount}
                  showDealerFee={!!dealer?.flags.showDealerFee}
                />
                {isEditable && isEditing && (
                  <>
                    <Col xs={12} sm={4} className="offer-section">
                      <div className="offer-section-title">Adjust Offer</div>
                      <div>
                        <div className="offer-row flex flex-wrap gap-10">
                          <div>
                            <div className="offer-label pt-10 pb-10">
                              Vehicle Sale Price
                              <Tooltip
                                width={200}
                                text={getVehicleSalesPriceInfo(props.car)}
                              >
                                <i className="vehiclePriceTooltip glyphicon glyphicon-info-sign" />
                              </Tooltip>
                            </div>
                            <PriceInput
                              placeholder="0"
                              className="offer-down-payment"
                              value={effectivePrice ?? 0}
                              onChange={newEffectivePrice => {
                                fetchResponseWithUpdatedData({
                                  effectivePrice:
                                    parseInt(newEffectivePrice, 10) || 0,
                                });
                              }}
                            />
                          </div>
                          <div>
                            <div className="offer-label pt-10 pb-10">Term</div>
                            <Select
                              value={response?.data?.months}
                              onChange={value => {
                                fetchResponseWithUpdatedData({
                                  months: parseInt(value, 10),
                                });
                              }}
                            >
                              {termRates.map(rate => (
                                <Select.Option
                                  value={rate.months.toString()}
                                  name={[
                                    `${rate.months} Months`,
                                    rate.apr
                                      ? `(APR: ${fmtPercent(rate.apr)})`
                                      : `(No Rate)`,
                                  ]
                                    .filter(Boolean)
                                    .join(' ')}
                                />
                              ))}
                            </Select>
                          </div>
                          <div>
                            <div className="offer-label pt-10 pb-10">
                              <span
                                className={showDownPaymentError ? 'error' : ''}
                              >
                                Down Payment
                              </span>
                              <DownPaymentTooltip
                                rates={downPaymentRates}
                                highlightedRateId={highlightedRate?.rateId}
                                isOverLimit={
                                  validations?.isDownPaymentOverLimit
                                }
                                isUnderLimit={
                                  validations?.isDownPaymentUnderLimit
                                }
                              />
                            </div>
                            <PriceInput
                              placeholder="0"
                              className={'offer-down-payment'}
                              value={response?.data?.downPayment ?? 0}
                              onChange={newDownPayment => {
                                fetchResponseWithUpdatedData({
                                  downPayment:
                                    parseInt(newDownPayment, 10) || 0,
                                });
                              }}
                            />
                          </div>
                          <div>
                            <div className="offer-label pt-10 pb-10">
                              <span style={{ marginRight: '5px' }}>
                                Net Trade
                              </span>
                              <span>
                                {isEmpty(tradeIns) ? (
                                  <span className="no-trades-available-message">
                                    No Trades Available
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    className="edit-trade-in-button"
                                    onClick={() => {
                                      setShowTradeInsModal(true);
                                    }}
                                  >
                                    Edit Trade In
                                  </button>
                                )}
                              </span>
                            </div>
                            <div className="offer-trade-amount">
                              {fmtPrice(netTrade ?? 0)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </Col>
                    <Col xs={12} sm={4} className="offer-section">
                      {response && (
                        <UpstartFinanceDetails
                          financeResponse={response}
                          financeAmountRates={financeAmountRates}
                          highlightedRate={highlightedRate}
                          dealer={dealer}
                        />
                      )}
                    </Col>
                    {(!responseIsAdjustedCounterOffer || isEditing) &&
                      productsAccessoriesAddOnsColumn}
                  </>
                )}
                {isEditable && props.car && isEditing && (
                  <Col xs={12} sm={6} className="offer-section">
                    <div className="offer-section-title">Add Products</div>
                    <Tabs
                      id="product-tabs"
                      defaultActiveKey={1}
                      className="product-tabs"
                    >
                      <Tab eventKey={1} title="Accessories">
                        <AccessoryList
                          fetching={accessoriesFetching}
                          accessories={applicableAccessories}
                          selectedAccessories={selectedAccessories}
                          toggleAccessory={accessory => {
                            fetchResponseWithUpdatedData({
                              selectedAccessories: toggleAccessory(accessory),
                            });
                          }}
                        />
                      </Tab>
                      <Tab eventKey={2} title="Protection Plans">
                        <div className="offer-protection-plan-list">
                          <ProtectionPlansList
                            fetching={protectionPlansFetching}
                            productList={productList}
                            products={products}
                            rates={rates || undefined}
                            termOptions={termOptions}
                            protectionPlanProductPriceOverrides={priceOverrides}
                            minMaxDebounceInEffect={minMaxDebounceInEffect}
                            productIdBeingEdited={productIdBeingEdited}
                            setProducts={(productMap: ProductMap) => {
                              setProducts(productMap);
                              fetchResponseWithUpdatedData({
                                selectedProducts: getSelectedProducts(
                                  bundles,
                                  productMap,
                                ),
                              });
                            }}
                            toggleProductSelect={(id: ProductId) => {
                              const {
                                bundleMap,
                                productMap,
                              } = toggleProductSelect(id);
                              fetchResponseWithUpdatedData({
                                selectedBundles: getSelectedBundles(bundleMap),
                                selectedProducts: getSelectedProducts(
                                  bundleMap,
                                  productMap,
                                ),
                              });
                            }}
                            toggleInCapCostFlag={() => {}} // Not used in Upstart
                            setProductIdBeingEdited={setProductIdBeingEdited}
                            setProtectionPlanProductOverrides={newPriceOverrides => {
                              fetchResponseWithUpdatedData({
                                protectionPlanProductPriceOverrides: newPriceOverrides,
                              });
                            }}
                            setMinMaxDebounceInEffect={
                              setMinMaxDebounceInEffect
                            }
                            productBackendCalculationVersion={
                              response?.data?.backendCalculationVersion
                            }
                            displayBackendBadge={
                              !!dealer?.flags?.displayBackendAmounts
                            }
                            isOnePayLease={false}
                          />
                        </div>
                      </Tab>
                      <Tab eventKey={3} title="Add Ons">
                        <AddOnsList
                          addOns={selectedAddOns}
                          isDMSFlowEnabled={isDMSFlowEnabled}
                          isDMSTypeCDK={isDMSTypeCDK}
                          setAddOns={newAddOns => {
                            fetchResponseWithUpdatedData({
                              selectedAddOns: newAddOns,
                            });
                          }}
                        />
                      </Tab>
                    </Tabs>
                  </Col>
                )}
                {!responseIsAdjustedCounterOffer &&
                  !isEditing &&
                  productsAccessoriesAddOnsColumn}
              </Row>
              <Row className="">
                {responseIsAdjustedCounterOffer &&
                  !isEditing &&
                  productsAccessoriesAddOnsColumn}
                <Col
                  xs={responseIsAdjustedCounterOffer ? 6 : 12}
                  sm={responseIsAdjustedCounterOffer ? 6 : 12}
                  className="offer-section"
                >
                  <div className="offer-section-title">Stipulations</div>
                  {asyncUpstartApplicationStatus.loading && (
                    <Loader size={'small'} active inline />
                  )}
                  {!asyncUpstartApplicationStatus.loading && (
                    <ul>
                      {getStipulationsElements({
                        stipulations,
                        hasStipulationGenerationCompleted,
                        hasUpdateInProgress,
                      })}
                    </ul>
                  )}
                </Col>
              </Row>
            </Grid>
          </div>
        )}
      </div>
      {response && showTradeInsModal && dealer && (
        <TradeInsModal
          tradeInFields={tradeInFields}
          initialTradeIns={response.data?.tradeIns ?? []}
          dealer={dealer}
          updateTradeIns={async newTradeIns => {
            fetchResponseWithUpdatedData({
              tradeIns: newTradeIns,
            });
          }}
          isOpen={showTradeInsModal}
          onClose={() => setShowTradeInsModal(false)}
          header="Trade-Ins To Apply"
          clearFauxTradeInOnDelete
          alwaysAllowTradeInUpdates
        />
      )}
      <OfferHistoryModal
        isOpen={showOfferHistoryModal}
        isLoading={asyncAuditResponse.loading}
        financeResponseAudit={asyncAuditResponse.result}
        onClose={() => setShowOfferHistoryModal(false)}
      />
    </>
  );
}

const getStipulationsElements = ({
  stipulations,
  hasStipulationGenerationCompleted,
  hasUpdateInProgress,
}: {
  stipulations: UpstartApplicationStatus['stipulations'];
  hasStipulationGenerationCompleted: boolean;
  hasUpdateInProgress: boolean;
}) => {
  if (stipulations && stipulations.length) {
    return stipulations.map(stip => <li key={stip.code}>{stip.title}</li>);
  }
  if (hasStipulationGenerationCompleted) {
    return <li>No stipulations required</li>;
  }
  if (hasUpdateInProgress) {
    return (
      <li>
        Stipulations generation still in progress. Please wait for sometime and
        refresh the page
      </li>
    );
  }
  return (
    <li>
      Stipulations generation has not completed yet. Please wait for sometime
      and refresh the page to see if Stipulations are needed or not
    </li>
  );
};

// eslint-disable-next-line
const mapStateToProps = (state: any) => ({
  isLoading: state.deals.fetching,
  deal: state.deals.deal,
  user: state.deals.user,
  car: getPrimarySelectedCar(state),
});

const mapDispatchToProps = (dispatch: Dispatch<{}>) => ({
  actions: bindActionCreators({ ...dealsActions, ...penActions }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(UpstartOffer);
